import api from './api';
import type {
  CustomerCreateAgendamentoContract,
  CustomerAgendamentoItemContract,
  EnderecoFaturamentoContract,
  EstadoClinicaContract,
  ClinicaAutoagendamentoContract,
  DatasDisponiveisAutoagendamentoContract,
  AutoagendamentoDataContract,
  ConfirmarAutoagendamentoPayloadContract,
  ConfirmacaoAutoagendamentoResponseContract,
  AutoagendamentoRHItemContract,
  CancelarAutoagendamentoResponseContract,
  HistoricoAgendamentoItemContract,
  ValidarPlanilhaResponseContract,
  EnviarWhatsappAgendamentoPayloadContract,
  EnviarWhatsappAgendamentoResponseContract,
  ReagendarParcialItemContract,
  ReagendarParcialPayloadContract,
  ReagendarParcialResponseContract,
} from '@sstexames/shared-types';

export type EnderecoFaturamentoDto = EnderecoFaturamentoContract;
export type CreateAgendamentoDto = CustomerCreateAgendamentoContract;
export type CustomerAgendamentoItem = CustomerAgendamentoItemContract;
export type EstadoClinica = EstadoClinicaContract;
export type ClinicaAutoagendamento = ClinicaAutoagendamentoContract;
export type DatasDisponiveisAutoagendamento = DatasDisponiveisAutoagendamentoContract;
export type AutoagendamentoData = AutoagendamentoDataContract;
export type ConfirmarAutoagendamentoPayload = ConfirmarAutoagendamentoPayloadContract;
export type ConfirmacaoAutoagendamentoResponse = ConfirmacaoAutoagendamentoResponseContract;
export type AutoagendamentoRHItem = AutoagendamentoRHItemContract;
export type CancelarAutoagendamentoResponse = CancelarAutoagendamentoResponseContract;
export type HistoricoAgendamentoItem = HistoricoAgendamentoItemContract;
export type ValidarPlanilhaResult = ValidarPlanilhaResponseContract;
export type EnviarWhatsappAgendamentoPayload = EnviarWhatsappAgendamentoPayloadContract;
export type EnviarWhatsappAgendamentoResponse = EnviarWhatsappAgendamentoResponseContract;


export interface AgendamentoPreviewResponse {
  colaboradores: Array<{
    colaborador_id: number;
    colaborador_nome: string;
    colaborador_cpf: string;
    clinica_id: number;
    clinica_nome: string;
    variacao_id: number;
    variacao_nome: string;
    exames: Array<{
      exame_id: number;
      exame_nome: string;
      preco: number;
      preco_original: number;
      is_exame_clinico_literal: boolean;
      is_zerado: boolean;
    }>;
    total: number;
    total_original: number;
  }>;
  total_geral: number;
  total_geral_original: number;
  subscription_info?: {
    has_active_subscription: boolean;
    contracted_lives: number;
    lives_used_in_cycle: number;
    lives_remaining: number;
    exame_clinico_included: boolean;
    can_upgrade_bracket: boolean;
    suggested_bracket_id?: number;
  };
}

export interface AgendamentoResponse {
  pedido_id: number;
  codigo_pedido: string;
  valor_total: number;
  valor: number;
  valor_final?: number;
  agendamentos_count: number;
}

class AgendamentosService {
  /**
   * Preview dos valores calculados antes de criar o agendamento
   */
  async preview(data: CreateAgendamentoDto): Promise<AgendamentoPreviewResponse> {
    const response = await api.post<AgendamentoPreviewResponse>('/agendamentos/preview', data);
    return response.data;
  }

  /**
   * Criar agendamento
   */
  async create(data: CreateAgendamentoDto): Promise<AgendamentoResponse> {
    console.log('📤 [AgendamentosService.create] Iniciando criação de agendamento...');
    console.log('📋 [AgendamentosService.create] Dados enviados:', {
      agendamentosCount: data.agendamentos?.length || 0,
      primeiroAgendamento: data.agendamentos?.[0],
      todosAgendamentos: data.agendamentos,
    });

    try {
      const response = await api.post<AgendamentoResponse>('/agendamentos', data);
      console.log('✅ [AgendamentosService.create] Resposta recebida:', {
        pedido_id: response.data.pedido_id,
        codigo_pedido: response.data.codigo_pedido,
        valor_total: response.data.valor_total,
        agendamentos_count: response.data.agendamentos_count,
        respostaCompleta: response.data,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { data?: unknown; status?: number; headers?: unknown }; stack?: string };
      console.error('❌ [AgendamentosService.create] Erro ao criar agendamento:', {
        error,
        message: err?.message,
        response: err?.response,
        responseData: err?.response?.data,
        responseStatus: err?.response?.status,
        responseHeaders: err?.response?.headers,
        requestData: data,
        stack: err?.stack,
      });
      throw error;
    }
  }

  /**
   * Buscar dados públicos do autoagendamento pelo token do colaborador
   */
  async getAutoagendamentoByToken(token: string): Promise<AutoagendamentoData> {
    const response = await api.get<AutoagendamentoData>(`/auto-agendamento/${token}`);
    return response.data;
  }

  /**
   * Listar estados com clínicas disponíveis para o token
   */
  async getEstadosAutoagendamento(token: string): Promise<EstadoClinica[]> {
    const response = await api.get<EstadoClinica[]>(`/auto-agendamento/${token}/estados`);
    return response.data;
  }

  /**
   * Listar cidades com clínicas disponíveis para o token no estado
   */
  async getCidadesAutoagendamento(token: string, estado: string): Promise<string[]> {
    const response = await api.get<string[]>(`/auto-agendamento/${token}/cidades`, {
      params: { estado },
    });
    return response.data;
  }

  /**
   * Listar bairros com clínicas disponíveis para o token no estado e cidade
   */
  async getBairrosAutoagendamento(token: string, estado: string, cidade: string): Promise<string[]> {
    const response = await api.get<string[]>(`/auto-agendamento/${token}/bairros`, {
      params: { estado, cidade },
    });
    return response.data;
  }

  /**
   * Listar clínicas filtradas por UF, cidade e opcionalmente bairro
   */
  async getClinicasAutoagendamento(
    token: string,
    params: { estado: string; cidade: string; bairro?: string },
  ): Promise<ClinicaAutoagendamento[]> {
    const response = await api.get<ClinicaAutoagendamento[]>(`/auto-agendamento/${token}/clinicas`, {
      params,
    });
    return response.data;
  }

  /**
   * Obter datas disponíveis de uma clínica
   */
  async getDatasAutoagendamento(token: string, clinicaId: number): Promise<DatasDisponiveisAutoagendamento> {
    const response = await api.get<DatasDisponiveisAutoagendamento>(
      `/auto-agendamento/${token}/clinica/${clinicaId}/datas`,
    );
    return response.data;
  }

  /**
   * Confirmar autoagendamento pelo colaborador
   */
  async confirmarAutoagendamento(
    token: string,
    payload: { clinica_id: number; data_agendamento: string; horario?: string },
  ): Promise<ConfirmacaoAutoagendamentoResponse> {
    const response = await api.post<ConfirmacaoAutoagendamentoResponse>(
      `/auto-agendamento/${token}/confirmar`,
      payload,
    );
    return response.data;
  }

  /**
   * RH: Listar todos os autoagendamentos da empresa
   */
  async getAutoagendamentosRH(): Promise<AutoagendamentoRHItem[]> {
    const response = await api.get<AutoagendamentoRHItem[]>('/agendamentos/auto-agendamentos');
    return response.data;
  }

  /**
   * RH: Reenviar lembrete de autoagendamento para o colaborador
   */
  async reenviarLembreteAutoagendamento(token: string): Promise<{ success: boolean; message: string; email_enviado: boolean }> {
    const response = await api.post<{ success: boolean; message: string; email_enviado: boolean }>(
      `/agendamentos/auto-agendamentos/${token}/reenviar-lembrete`,
    );
    return response.data;
  }

  /**
   * RH: Reenviar link de confirmação de presença (WhatsApp e E-mail) para agendamentos do dia
   */
  async reenviarLembretePresenca(agendamentoId: number): Promise<{ success: boolean; message: string; email_enviado?: boolean; whatsapp_enviado?: boolean }> {
    const response = await api.post<{ success: boolean; message: string; email_enviado?: boolean; whatsapp_enviado?: boolean }>(
      `/agendamentos/${agendamentoId}/reenviar-presenca`,
    );
    return response.data;
  }

  /**
   * RH: Cancelar autoagendamento pendente
   */
  async cancelarAutoagendamento(token: string): Promise<CancelarAutoagendamentoResponse> {
    const response = await api.post<CancelarAutoagendamentoResponse>(
      `/agendamentos/auto-agendamentos/${token}/cancelar`,
    );
    return response.data;
  }

  /**
   * RH: Reagendar autoagendamento pendente ou expirado para uma nova data limite
   */
  async reagendarAutoagendamento(
    token: string,
    novaData: string,
    motivo?: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `/agendamentos/auto-agendamentos/${token}/reagendar`,
      {
        nova_data: novaData,
        motivo,
      },
    );
    return response.data;
  }

  /**
   * RH: Histórico consolidado de todos os agendamentos
   */
  async getHistorico(): Promise<HistoricoAgendamentoItem[]> {
    const response = await api.get<HistoricoAgendamentoItem[]>('/agendamentos/historico');
    return response.data;
  }

  /**
   * RH: Baixar planilha modelo (.xlsx) com cargos, setores e clínicas válidas da empresa
   */
  async downloadTemplatePlanilha(clinicaIds?: number[], tipo?: 'completo' | 'admissional' | 'existentes'): Promise<void> {
    const params: Record<string, string> = {};
    if (clinicaIds && clinicaIds.length > 0) {
      params.clinicas = clinicaIds.join(',');
    }
    if (tipo) {
      params.tipo = tipo;
    }
    const response = await api.get('/agendamentos/template-planilha', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'modelo_agendamento_lote.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * RH: Validar linhas importadas de planilha contra regras e cargos da empresa
   */
  async validarPlanilha(rows: Record<string, unknown>[]): Promise<ValidarPlanilhaResult> {
    const response = await api.post<ValidarPlanilhaResult>('/agendamentos/validar-planilha', { rows });
    return response.data;
  }

  /**
   * RH: Cancelar agendamento (direto RH ou geral) pelo ID
   */
  async cancelarAgendamentoRH(id: number, motivo?: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(`/agendamentos/${id}/cancelar`, { motivo });
    return response.data;
  }

  /**
   * RH: Reagendar agendamento (direto RH) pelo ID
   */
  async reagendarAgendamentoRH(
    id: number,
    nova_data: string,
    horario?: string,
    nova_clinica_id?: number,
    motivo?: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(`/agendamentos/${id}/reagendar`, {
      nova_data,
      horario,
      nova_clinica_id,
      motivo,
    });
    return response.data;
  }

  /**
   * Envia template oficial de agendamento no WhatsApp via AiDesk (com contingência interna)
   */
  async enviarWhatsappAgendamento(
    payload: EnviarWhatsappAgendamentoPayload,
  ): Promise<EnviarWhatsappAgendamentoResponse> {
    const response = await api.post<EnviarWhatsappAgendamentoResponse>(
      '/agendamentos/enviar-whatsapp',
      payload,
    );
    return response.data;
  }

  /**
   * Baixar ASO do agendamento (físico ou gerado dinamicamente com ASO digital)
   */
  async downloadAso(id: number): Promise<Blob> {
    const response = await api.get(`/agendamentos/${id}/aso`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * RH: Obter itens elegíveis para reagendamento parcial / sub-agendamento
   */
  async obterItensReagendamento(
    id: number
  ): Promise<{ agendamento_id: number; colaborador_nome: string; itens: ReagendarParcialItemContract[] }> {
    const response = await api.get<{
      agendamento_id: number;
      colaborador_nome: string;
      itens: ReagendarParcialItemContract[];
    }>(`/agendamentos/${id}/itens-reagendamento`);
    return response.data;
  }

  /**
   * RH: Reagendar parcialmente exames específicos (cria sub-agendamento)
   */
  async reagendarParcial(
    id: number,
    payload: ReagendarParcialPayloadContract
  ): Promise<ReagendarParcialResponseContract> {
    const response = await api.post<ReagendarParcialResponseContract>(
      `/agendamentos/${id}/reagendar-parcial`,
      payload
    );
    return response.data;
  }
}

export const agendamentosService = new AgendamentosService();


